import { Plus, MessageCircle } from "lucide-react";
import { useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { motion, AnimatePresence } from "framer-motion";
import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/sections/CTASection";
import { brand, capabilities } from "@/data/content";
import { servicesSchema } from "@/data/seo";

const SERVICES_CRUMBS = [{ name: "Services", path: "/services" }];
const SERVICES_SCHEMA = [servicesSchema()];

function SubserviceRow({ point }) {
  const [open, setOpen] = useState(false);
  const label = typeof point === "string" ? point : point.label;
  const detail = typeof point === "string" ? "" : point.detail;
  return (
    <li className="border-b border-line">
      <button
        type="button"
        onClick={() => detail && setOpen((v) => !v)}
        data-testid={`subservice-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className="flex w-full items-start gap-2 py-3 text-left text-ink transition-colors hover:text-accent"
      >
        <Plus
          size={14}
          className={`mt-1.5 shrink-0 text-accent transition-transform duration-300 ${open ? "rotate-45" : ""}`}
        />
        <span className="text-lg">{label}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && detail && (
          <motion.p
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
            className="overflow-hidden pl-6 pr-2 text-base leading-relaxed text-muted"
          >
            <span className="block pb-4">{detail}</span>
          </motion.p>
        )}
      </AnimatePresence>
    </li>
  );
}

export default function Services() {
  useSeo({
    title: "Marketing Services: Brand, Digital, PR, Events & Exhibitions",
    description:
      "Brand strategy, creative, digital marketing, PR, events, activations and exhibition design from HashTag Marketing, an integrated agency in Gurgaon, Delhi NCR.",
    path: "/services",
    breadcrumbs: SERVICES_CRUMBS,
    schema: SERVICES_SCHEMA,
  });
  return (
    <>
      <PageHeader
        eyebrow="Services"
        titleLines={["What we", "do."]}
        intro="One team across five disciplines. Engage us for a single capability or as your end-to-end integrated agency."
      />
      <section className="mx-auto max-w-[1600px] px-5 py-14 sm:py-16 md:py-20 lg:py-24 xl:py-28 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        {capabilities.map((cap, i) => (
          <motion.div
            key={cap.n}
            id={cap.title.toLowerCase().replace(/\s+/g, "-")}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
            className="grid gap-6 border-t border-line py-10 sm:py-12 md:grid-cols-12 md:gap-8 md:py-14"
          >
            <div className="md:col-span-1">
              <span className="font-serif text-3xl italic text-accent">{cap.n}</span>
            </div>
            <div className="md:col-span-5">
              <h2 className="font-display text-4xl font-extrabold text-ink md:text-6xl">{cap.title}</h2>
              <p className="mt-3 text-xs font-bold uppercase tracking-[0.14em] text-muted">{cap.tag}</p>
              {cap.oneLiner && (
                <p className="mt-6 max-w-md font-serif text-xl italic leading-snug text-ink md:text-2xl">{cap.oneLiner}</p>
              )}
              <p className="mt-4 max-w-md text-base leading-relaxed text-muted md:text-lg">{cap.body}</p>
            </div>
            <div className="md:col-span-6">
              <ul className="grid gap-x-8 sm:grid-cols-2">
                {cap.points.map((p) => (
                  <SubserviceRow key={typeof p === "string" ? p : p.label} point={p} />
                ))}
              </ul>
            </div>
          </motion.div>
        ))}

        <div className="border-t border-line pt-10 md:pt-12">
          <div
            data-testid="services-whatsapp-band"
            className="flex flex-col items-start justify-between gap-6 bg-ink px-6 py-8 text-bone sm:px-8 sm:py-10 md:flex-row md:items-center md:px-10 md:py-12 lg:px-12 lg:py-14"
          >
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-accent">Want detailed information?</p>
              <p className="mt-3 font-display text-3xl font-extrabold leading-tight md:text-5xl">
                WhatsApp us.
              </p>
              <p className="mt-3 max-w-md text-base leading-relaxed text-bone/70">
                Tell us what you're building — we'll walk you through the right mix of services for your brand.
              </p>
            </div>
            <a
              href={`https://wa.me/${brand.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-whatsapp-band-cta"
              className="inline-flex shrink-0 items-center gap-2 border border-accent bg-accent px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-bone transition-colors hover:bg-bone hover:text-ink hover:border-bone"
            >
              <MessageCircle size={18} /> WhatsApp us
            </a>
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );
}
